import { Consent, CreateAvatarStoreUnion } from '@/pages/avatar/common/types';
import createAvatarStore from '@/pages/avatar/CreatePage/CreateAvatarStore';
import premiumPlusCreateAvatarStore from '@/pages/avatar/PremiumPlusCreatePage/CreateAvatarStore';
import { useAuth } from '@/services/auth';
import { createContext, ReactNode, useContext } from 'react';
import { AvatarType } from './types';

const CreateAvatarContext = createContext<CreateAvatarStoreUnion | null>(null);

const getFormattedUserName = (userName: string) => {
    return userName
        .replace(/[-]/g, ' ') // Replace hyphens with spaces
        .replace(/[.]/g, ' ') // Replace periods with spaces
        .replace(/[_]/g, ' ') // Replace underscores with spaces
        .replace(/[^a-zA-Z\s]/g, '') // Remove all other special characters
        .replace(/\s+/g, ' ') // Collapse multiple spaces into one
        .trim(); // Remove leading and trailing spaces
};

export const CreateAvatarStoreProvider = ({
    children,
    type,
    allowSelection,
    initialValues = {},
}: {
    children: ReactNode;
    type: AvatarType;
    allowSelection?: boolean;
    initialValues?: { id?: string; footageUrl?: string; consent?: Consent | null };
}) => {
    const { user } = useAuth();
    const store = type === AvatarType.Express ? createAvatarStore : premiumPlusCreateAvatarStore;

    const username = user?.given_name && user?.family_name ? `${user.given_name} ${user.family_name}` : undefined;
    // this should work only if this component is the main one rendered when the page loads (i.e. top component).
    // otherwise this should break and make the store to be initialized on each render
    store.init({
        ...initialValues,
        username: username ? getFormattedUserName(username) : username,
        skipConsent: user?.features.includes('consents:skip'),
        allowSelection,
    });

    return <CreateAvatarContext.Provider value={store}>{children}</CreateAvatarContext.Provider>;
};

export const useCreateAvatarStore = () => {
    const context = useContext(CreateAvatarContext);

    if (!context) {
        throw new Error('useCreateAvatarStore must be used within a CreateAvatarStoreProvider');
    }

    return context;
};
